import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, Overline } from "@/components/Reveal";
import { useSettings } from "@/contexts/SettingsContext";

const PRODUCTION_GROWTH = [
  { year: "2001", ton: "3,8" },
  { year: "2002", ton: "5" },
  { year: "2003", ton: "7,4" },
  { year: "2004", ton: "9,4" },
  { year: "2005", ton: "11" },
  { year: "2006", ton: "11,8" },
  { year: "2007", ton: "11,8" },
  { year: "2008", ton: "17,7" },
];

export const About = () => {
  const { settings } = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  const historyParagraphs = (settings.about_history || "")
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <section id="tentang" className="relative bg-cream py-24 md:py-36 px-6 md:px-10">
      <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Sticky image column */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 overflow-hidden rounded-[2rem] arch-top">
            <motion.img
              style={{ y, objectPosition: `center ${settings.about_image_position ?? 50}%` }}
              src={settings.about_image}
              alt="Founder Samsi Madu"
              className="w-full h-[420px] md:h-[560px] object-cover scale-110"
            />
          </div>
        </div>

        {/* Content column */}
        <div className="lg:col-span-7 lg:pl-8">
          <Reveal>
            <Overline>Tentang Kami</Overline>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-ink mb-6">
              {settings.about_heading}
            </h2>
          </Reveal>

          {historyParagraphs.length > 0 ? (
            <div className="space-y-5 mb-14">
              {historyParagraphs.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p className="text-base md:text-lg text-stone2 leading-relaxed max-w-xl">{p}</p>
                </Reveal>
              ))}
            </div>
          ) : (
            <Reveal>
              <p className="text-base md:text-lg text-stone2 leading-relaxed max-w-xl mb-14">
                {settings.about_intro}
              </p>
            </Reveal>
          )}

          {/* Production growth strip */}
          <Reveal delay={0.1}>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-honey-dark mb-4">
                Pertumbuhan Produksi Madu
              </p>
              <div className="flex gap-6 overflow-x-auto pb-2 -mx-1 px-1">
                {PRODUCTION_GROWTH.map((s) => (
                  <div key={s.year} className="shrink-0 text-center">
                    <div className="font-display text-3xl md:text-4xl text-ink tracking-tight">
                      {s.ton}
                      <span className="text-base md:text-lg text-honey-dark ml-1">ton</span>
                    </div>
                    <div className="mt-1 text-xs text-stone2">{s.year}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
